import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [admin, setAdmin] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("e1_token"));
  const [isAdmin, setIsAdmin] = useState(localStorage.getItem("e1_is_admin") === "true");
  const [isGoogleAuth, setIsGoogleAuth] = useState(localStorage.getItem("e1_google_auth") === "true");
  const [loading, setLoading] = useState(true);
  const initializedRef = useRef(false);
  const isLoggingOut = useRef(false);

  const logout = useCallback(async () => {
    if (isLoggingOut.current) return;
    isLoggingOut.current = true;
    
    // If Google auth, also call the logout endpoint to clear cookie
    if (isGoogleAuth) {
      try {
        await axios.post(`${API}/auth/google/logout`, {}, { withCredentials: true });
      } catch (e) {
        console.log("Google logout error (non-blocking):", e);
      }
    }
    
    localStorage.removeItem("e1_token");
    localStorage.removeItem("e1_is_admin");
    localStorage.removeItem("e1_google_auth");
    setToken(null);
    setUser(null);
    setAdmin(null);
    setIsAdmin(false);
    setIsGoogleAuth(false);
    initializedRef.current = false;
    
    setTimeout(() => {
      isLoggingOut.current = false;
    }, 1000);
  }, [isGoogleAuth]);

  // Set user from Google auth callback
  const setGoogleUser = useCallback((userData, jwtToken) => {
    setUser(userData);
    setToken(jwtToken);
    setIsAdmin(false);
    setIsGoogleAuth(true);
    setAdmin(null);
    initializedRef.current = true;
    setLoading(false);
  }, []);

  // Only run once on mount or when token changes from login/logout
  useEffect(() => {
    const initAuth = async () => {
      // CRITICAL: If returning from OAuth callback, skip the /me check.
      // AuthCallback will exchange the session_id and establish the session first.
      if (window.location.hash?.includes('session_id=')) {
        setLoading(false);
        return;
      }
      
      // Skip if already initialized
      if (initializedRef.current) {
        setLoading(false);
        return;
      }
      
      const storedToken = localStorage.getItem("e1_token");
      const storedIsAdmin = localStorage.getItem("e1_is_admin") === "true";
      const storedIsGoogle = localStorage.getItem("e1_google_auth") === "true";
      
      if (storedToken) {
        try {
          // For Google auth, try the Google /me endpoint first, then fall back to regular
          const endpoint = storedIsAdmin ? "/admin/me" : (storedIsGoogle ? "/auth/google/me" : "/auth/me");
          const response = await axios.get(`${API}${endpoint}`, {
            headers: { Authorization: `Bearer ${storedToken}` },
            withCredentials: true
          });
          
          if (storedIsAdmin) {
            setAdmin(response.data);
            setUser(null);
          } else {
            setUser(response.data);
            setAdmin(null);
          }
          setToken(storedToken);
          setIsAdmin(storedIsAdmin);
          setIsGoogleAuth(storedIsGoogle);
          initializedRef.current = true;
        } catch (error) {
          console.error("Auth init error:", error);
          // If Google auth failed, try regular auth
          if (storedIsGoogle && !storedIsAdmin) {
            try {
              const response = await axios.get(`${API}/auth/me`, {
                headers: { Authorization: `Bearer ${storedToken}` }
              });
              setUser(response.data);
              setAdmin(null);
              setToken(storedToken);
              setIsAdmin(false);
              // Keep isGoogleAuth as-is so logout still calls /auth/google/logout
              setIsGoogleAuth(storedIsGoogle);
              initializedRef.current = true;
            } catch (e) {
              // Both failed, check if token is explicitly invalid
              const isInvalidToken = 
                error.response?.status === 401 && 
                error.response?.data?.detail?.toLowerCase()?.includes("token");
              
              if (isInvalidToken) {
                console.log("Token invalid, logging out");
                logout();
              } else {
                console.log("Non-token error, keeping session:", error.response?.status);
                initializedRef.current = true;
              }
            }
          } else {
            // ONLY logout if token is explicitly invalid (401 with specific message)
            const isInvalidToken = 
              error.response?.status === 401 && 
              error.response?.data?.detail?.toLowerCase()?.includes("token");
            
            if (isInvalidToken) {
              console.log("Token invalid, logging out");
              logout();
            } else {
              console.log("Non-token error, keeping session:", error.response?.status);
              initializedRef.current = true;
            }
          }
        }
      }
      setLoading(false);
    };
    
    initAuth();
  }, []); // Empty dependency array - only run once on mount

  const login = async (phone, password) => {
    const response = await axios.post(`${API}/auth/login`, { phone, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("e1_token", newToken);
    localStorage.setItem("e1_is_admin", "false");
    setToken(newToken);
    setIsAdmin(false);
    setUser(userData);
    setAdmin(null);
    initializedRef.current = true;
    return response.data;
  };

  const adminLogin = async (phone, password) => {
    const response = await axios.post(`${API}/admin/login`, { phone, password });
    const { token: newToken, admin: adminData } = response.data;
    localStorage.setItem("e1_token", newToken);
    localStorage.setItem("e1_is_admin", "true");
    setToken(newToken);
    setIsAdmin(true);
    setAdmin(adminData);
    setUser(null);
    initializedRef.current = true;
    return response.data;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    return response.data;
  };

  const refreshUser = async () => {
    if (token && !isAdmin) {
      try {
        const endpoint = isGoogleAuth ? "/auth/google/me" : "/auth/me";
        const response = await axios.get(`${API}${endpoint}`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });
        setUser(response.data);
      } catch (error) {
        console.error("Refresh user error:", error);
        // Never logout on refresh errors
      }
    }
  };

  // Google login function - redirects to Emergent Auth
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleLogin = () => {
    const redirectUrl = window.location.origin + '/usuario';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const value = {
    user,
    admin,
    token,
    isAdmin,
    isGoogleAuth,
    loading,
    login,
    adminLogin,
    register,
    logout,
    refreshUser,
    googleLogin,
    setGoogleUser,
    isAuthenticated: !!token && (!!user || !!admin)
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
